import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useRef } from "react";

import { ApiError, type ApiClient } from "../api/client";
import type { AuthClient } from "../auth/authClient";
import { SessionRenewalNeededError } from "../auth/errors";
import { useTheme } from "../theme/theme-context";
import { useGoToMicrosoft } from "./goToMicrosoft";
import { LoadingScreen, MessageScreen, RenewingScreen } from "./Screens";
import { ME_QUERY_KEY, type Session, SessionContext } from "./session-context";

/**
 * Loads the signed-in user from the API and provides the session to the app.
 *
 * Straight after an interactive sign-in (`freshSignIn`), the first call is
 * POST /api/v1/me/sign-in, which records the sign-in in the audit log and
 * returns the same user as GET /api/v1/me (ADR 0006 section 4). Once it has
 * succeeded, every reload of the user (on focus, say) is GET /api/v1/me: the
 * sign-in is recorded once a session (#255).
 *
 * The user's saved theme choice, once known, wins over this browser's, and the
 * browser keeps it for the next first paint (ADR 0041).
 */
export function SessionGate({
  auth,
  api,
  freshSignIn,
  children,
}: {
  auth: AuthClient;
  api: ApiClient;
  freshSignIn: boolean;
  children: ReactNode;
}) {
  const signInRecorded = useRef(false);
  const me = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      if (!freshSignIn || signInRecorded.current) {
        return api.getMe();
      }
      const user = await api.recordSignIn();
      signInRecorded.current = true;
      return user;
    },
    retry: (failures, error) =>
      !(error instanceof ApiError && error.status < 500) &&
      !(error instanceof SessionRenewalNeededError) &&
      failures < 2,
    staleTime: 5 * 60 * 1000,
  });

  const { setPreference } = useTheme();
  const savedTheme = me.data?.theme ?? null;
  useEffect(() => {
    if (savedTheme !== null) {
      setPreference(savedTheme);
    }
  }, [savedTheme, setPreference]);

  // Opening the app with a sign-in to renew: nothing is typed yet, so it goes
  // to Microsoft in this tab, as it always has (ADR 0039). An open page never
  // does; it shows RenewalBanner instead (#252; ADR 0093).
  const renewAtStart =
    me.data === undefined && me.error instanceof SessionRenewalNeededError;
  const { failed: renewFailed, go: goRenew } = useGoToMicrosoft();
  useEffect(() => {
    if (renewAtStart) {
      goRenew(() => auth.renewInThisTab());
    }
  }, [renewAtStart, auth, goRenew]);
  // Sign in again, or out (#865).
  const leave = useGoToMicrosoft();

  const session = useMemo<Session | null>(
    () => (me.data ? { auth, api, me: me.data } : null),
    [auth, api, me.data],
  );

  if (session) {
    return <SessionContext value={session}>{children}</SessionContext>;
  }
  if (renewAtStart && renewFailed !== null) {
    return (
      <MessageScreen
        title="Your sign-in needs renewing"
        message={renewFailed}
        action={{
          label: "Try again",
          onClick: () => {
            goRenew(() => auth.renewInThisTab());
          },
        }}
      />
    );
  }
  if (renewAtStart) {
    return <RenewingScreen />;
  }
  if (me.error instanceof ApiError && me.error.status === 403) {
    return (
      <MessageScreen
        title="No access to Axon Time"
        message={
          me.error.detail ??
          "Your account has not been given access to Axon Time."
        }
        action={{
          label: "Sign out",
          onClick: () => {
            leave.go(() => auth.signOut());
          },
        }}
        failed={leave.failed}
      />
    );
  }
  if (me.error instanceof ApiError && me.error.status === 401) {
    return (
      <MessageScreen
        title="Your session has ended"
        message="Sign in again to continue."
        action={{
          label: "Sign in again",
          onClick: () => {
            leave.go(() => auth.signIn());
          },
        }}
        failed={leave.failed}
      />
    );
  }
  if (me.error) {
    return (
      <MessageScreen
        title="Something went wrong"
        message="Axon Time could not load your account. Check your connection and try again."
        action={{
          label: "Try again",
          onClick: () => {
            void me.refetch();
          },
        }}
      />
    );
  }
  return <LoadingScreen />;
}
